/****************************************************
 * Module Name:  PacketReceiveMaster.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { useState } from "react";
import { DataGridPro, GridToolbar } from "@mui/x-data-grid-pro";
import { Avatar, Box, Button, Chip, Tooltip } from "@mui/material";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import renderCellExpand from "../common/dataGridCellExpand";
import Utility from "../../utility/utility";
import { useEffect } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import PacketHistoryModal from "../common/packetHistoryModal";
import Constants from "../../utility/constants";
import TabMenuSnackbar from "../common/tabMenuSnackbar";
import AddPacketReceiveModal from "./addPacketReceiveModal";
import CommonStoneSelection from "../common/commonStoneSelection";
import KapanHistoryModal from "../manufacturing/operations/kapanHistoryModal";
import { RiUserReceived2Fill } from "react-icons/ri";
import PacketProxy from "../../utility/proxy/manager/manufacturingProxy/packetProxy";
import PageTitle from "../common/pageTitle";

const PacketReceiveMaster = () => {
    const [listData, setListData] = useState([]);

    const [approveRejectListLoader, setApproveRejectListLoader] = useState(false);

    const [selectionModel, setSelectionModel] = useState([]);

    const [showPacketDetailModal, setShowPacketDetailModal] = useState(false);

    const [errorSnackbar, setErrorSnackbar] = useState(false);
    const [showReceiveModal, setShowReceiveModal,] = useState(false);
    const [sourceData, setSourceData,] = useState({});
    const [loadingButton, setLoadingButton] = useState(false);

    const [packetId, setPacketId] = useState("");
    const [kapanId, setKapanId] = useState("");

    const [showKapanDetailModal, setShowKapanDetailModal] = useState(false);

    const [totalPacket, setTotalPacket] = useState(0);
    const [totalCarats, setTotalCarats] = useState(0);
    const [totalPrice, setTotalPrice] = useState(0);
    const [avgPrice, setAvgPrice] = useState(0);

    const [showSnackbar, setShowSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarVariant, setSnackbarVariant] = useState("");
    const [columnNames, setColumnNames] = useState([]);

    // eslint-disable-next-line
    const [local_Permission, setLocal_Permission] = useState(
        Constants.USER_PERMISSION.filter(
            (iPermission) =>
                iPermission.permissionid ===
                Constants.PERMISSIONS.MANUFACTURING_OPERATION_PACKETRECEIVE
        )
    );

    useEffect(() => {
        PageTitle("MANUFACTURING_OPERATION_PACKETRECEIVE");
        handleSearch();
    }, []);

    const handleSearch = () => {
        setApproveRejectListLoader(true);
        PacketProxy.GetAllMyPacketToreceive((response) => {
            if (response) {
                const uniqueFilterValues = {
                    user: Utility.getDistinctOptions(response, "username"),
                    department: Utility.getDistinctOptions(response, "department_name"),
                    process: Utility.getDistinctOptions(response, "processname"),
                };
                const combinedFilterValues = {
                    user: uniqueFilterValues.user.map(value => {
                        const matchingItem = response.find(item => item.username === value);
                        return matchingItem ? `${matchingItem.userid} | ${value}` : value;
                    }),
                    department: uniqueFilterValues.department.map(value => {
                        const matchingItem = response.find(item => item.department_name === value);
                        return matchingItem ? `${matchingItem.department_id} | ${value}` : value;
                    }),
                    processname: uniqueFilterValues.process.map(value => {
                        const matchingItem = response.find(item => item.processname === value);
                        return matchingItem ? `${matchingItem.processid} | ${value}` : value;
                    }),
                };

                const updatedResponse = response.map(iData => ({
                    ...iData,
                    embededkey: iData.kapanid + "_" + iData.packetid
                }));

                prepareColumns(combinedFilterValues);
                setListData(updatedResponse);
                setSelectionModel([])
                setApproveRejectListLoader(false);
                getSummaryReport(response);
            } else {
                setApproveRejectListLoader(false);
                setErrorSnackbar(true)
            }
        });
    };

    const prepareColumns = (uniqueFilterValues) => {
        const columnNames = columnsWithoutPermission.map(woPermissionColumn => {
            let guids = [];

            // Determine which GUIDs (permissions) should be added based on the column field
            switch (woPermissionColumn.field) {
                case "packetid":
                    guids = [
                        Constants.COLUMN_PERMISSION.MFG_PACKET_CARATS,
                        Constants.COLUMN_PERMISSION.MFG_PACKET_PRICE,
                        Constants.COLUMN_PERMISSION.MFG_PACKET_CREATEDAT,
                        Constants.COLUMN_PERMISSION.MFG_PACKET_UPDATEDAT,
                        Constants.COLUMN_PERMISSION.MFG_PACKET_USER,
                        Constants.COLUMN_PERMISSION.MFG_PACKET_DEPARTMENT,
                        Constants.COLUMN_PERMISSION.MFG_PACKET_NEXT_DEPARTMENT_HEAD,
                        Constants.COLUMN_PERMISSION.MFG_PACKET_NEXT_DEPARTMENT,
                    ];
                    break;
                default:
                    guids = [];
            }

            // Retrieve valid permission columns
            return fetchColumnDataFromGuid(guids, woPermissionColumn, columnsWithPermission, uniqueFilterValues);

        }).flat(); // Flatten the array

        // Update state with the prepared columns
        setColumnNames(columnNames);
    };

    const fetchColumnDataFromGuid = (guids, currentColumn, columnsWithPermission, uniqueFilterValues) => {
        const permissionColumns = guids
            .map(id => Constants.USER_COLUMN_PERMISSION.find(permission => permission === id)) // Match permissions
            .filter(Boolean) // Remove undefined values (invalid permissions)
            .map(guid => {
                const columnData = columnsWithPermission[guid]; // Get column details

                // If the columnData is missing or an empty object, skip adding it
                if (!columnData || Object.keys(columnData).length === 0) {
                    return null;
                }

                if (columnData.field === "nextdepartmenthead") {
                    columnData.type = "singleSelect";
                    columnData.valueOptions = uniqueFilterValues ? uniqueFilterValues.nextdepartmenthead : [];
                    columnData.filterOperators = Utility.customFilters(uniqueFilterValues, columnData.field, 'next_department_head_id', 'nextdepartmenthead');
                }
                if (columnData.field === "nextdepartmentname") {
                    columnData.type = "singleSelect";
                    columnData.valueOptions = uniqueFilterValues ? uniqueFilterValues.nextdepartmentname : [];
                    columnData.filterOperators = Utility.customFilters(uniqueFilterValues, columnData.field, 'next_department_id', 'nextdepartmentname');
                }
                else {

                    // Retrieve unique filter values for the column, defaulting to an empty array
                    const filterData = uniqueFilterValues[columnData.field] || [];

                    return {
                        ...columnData,  // Copy all existing column properties
                        valueOptions: filterData,  // Assign filter values
                    };
                }

                return columnData;
            })
            .filter(Boolean); // Remove any null values from the list

        if (currentColumn.field === "processname") {
            currentColumn.type = "singleSelect";
            currentColumn.valueOptions = uniqueFilterValues ? uniqueFilterValues.process : [];
            currentColumn.filterOperators = Utility.customFilters(uniqueFilterValues, currentColumn.field, 'processid', 'processname');
        }
        // Modify the original column before adding it to the list
        const modifiedColumn = {
            ...currentColumn,  // Retain original column properties
            valueOptions: uniqueFilterValues[currentColumn.field] || [], // Assign filter values
        };

        // Return an array with the modified column and any valid permission columns
        return [modifiedColumn, ...permissionColumns];
    };

    const getSummaryReport = (data) => {
        if (!Array.isArray(data)) return;
        let totalPackets = data.length;
        let carats = 0;
        let price = 0;
        data.forEach((item) => {
            carats += item.carats;
            price += item.carats * item.price;
        });
        let avgprice = price / carats;

        setTotalPacket(totalPackets);
        setTotalPrice(price);
        setTotalCarats(carats);
        setAvgPrice(avgprice);
    }

    const handleValidation = (data) => {
        //TODO do validation if needed here
        return true;
    };

    const markReceive = (IssueData) => {
        setLoadingButton(true)
        // Creating new issue, and once issue is created on call refresh the list of current issue
        PacketProxy.MarkPacketReceive(IssueData, (response) => {
            if (response) {
                handleSearch()
                setLoadingButton(false)
                setShowReceiveModal(false)
                setSnackbar(true, response.errorMessage, response.status);
            } else {
                setErrorSnackbar(true)
                setLoadingButton(false)
                setSnackbar(true, Constants.APiFailErrorMessage, "error");
            }
        });
    };

    const setSnackbar = (showSnackbar, snackbarMessage, snackbarVariant) => {
        setShowSnackbar(showSnackbar);
        setSnackbarMessage(snackbarMessage);
        setSnackbarVariant(snackbarVariant);
    }

    const handlePacketDetailModal = (params, kapanid) => {
        setPacketId(params);
        setKapanId(kapanid);
        setShowPacketDetailModal(true);
    };

    const handleKapanDetailModal = (params) => {
        setKapanId(params);
        setShowKapanDetailModal(true);
    };

    const handleSelection = (packetIds) => {
        let lstKapanId_PacketId = [];
        packetIds.forEach(iData => {
            if (iData.includes("_")) {
                lstKapanId_PacketId.push(iData);
            } else {
                let lstRecords = listData.filter(iRow => iRow.packetid === iData);
                if (lstRecords.length > 0)
                    lstRecords.forEach(iRow => lstKapanId_PacketId.push(iRow.embededkey));
            }
        });
        setSelectionModel([...selectionModel, ...lstKapanId_PacketId])
    };

    const columnsWithPermission = {
        "0effedb5-9d19-4964-b11f-e722ffd19f2c": {
            field: "carats",
            headerName: "CARATS",
            headerAlign: "center",
            align: "center",
            minWidth: 100,
            flex: 1,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => params.value,
        },
        "da017870-3fdd-4560-9522-6cc693c6565d": {
            field: "price",
            headerName: "PPC",
            headerAlign: "center",
            align: "right",
            width: 70,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.price)
        },
        "43e79a8b-ec36-4c61-9d73-a7968fea5b3b": {
            field: "createdat",
            headerName: "CREATED AT",
            headerAlign: "center",
            align: "center",
            minWidth: 180,
            flex: 1,
            type: "dateTime",
            valueGetter: (params) => {
                if (params.value) {
                    return new Date(params.value);
                }
                return null;
            },
            renderCell: (params) => Utility.ConvertUtcToLocalTime(params.value),
        },
        "49fb0c84-687f-4940-88f3-ce7312c97b1a": {
            field: "updatedat",
            headerName: "UPDATED AT",
            headerAlign: "center",
            align: "center",
            minWidth: 180,
            flex: 1,
            type: "dateTime",
            valueGetter: (params) => {
                if (params.value) {
                    return new Date(params.value);
                }
                return null;
            },
            renderCell: (params) => Utility.ConvertUtcToLocalTime(params.value),
        },
        "366b9c15-7b22-4ec5-99ad-ec3f1e17c3cd": {
            field: "user",
            headerName: "USER",
            headerAlign: "center",
            align: "center",
            minWidth: 150,
            flex: 1,
            type: "singleSelect",
            filterOperators: Utility.combinedFilters(),
            valueGetter: (params) => params.row.userid + " | " + params.row.username,
            renderCell: renderCellExpand
        },
        "f1b9e3fa-fa0b-4439-9ead-a7d3c0d6d63f": {
            field: "department",
            headerName: "DEPARTMENT",
            headerAlign: "center",
            align: "center",
            minWidth: 150,
            flex: 1,
            type: "singleSelect",
            filterOperators: Utility.combinedFilters(),
            valueGetter: (params) => params.row.department_id + " | " + params.row.department_name,
            renderCell: renderCellExpand
        },
        "5b8746c7-e52a-4488-b65f-095b4cef0a1a": {
            field: "nextdepartmenthead",
            headerName: "ISSUED DEPARTMENT HEAD",
            headerAlign: "center",
            align: "center",
            minWidth: 150,
            flex: 1,
            renderCell: (params) => (
                params.row.next_department_head_id ?
                    <Box sx={{ minHeight: "auto", mt: 1 }}>
                        <Chip
                            avatar={<Avatar>{params.row.next_department_head_id}</Avatar>}
                            label={params.row.nextdepartmenthead}
                            sx={{ margin: "0px 10px 10px 0px" }}
                        />
                    </Box>
                    : '-'
            ),
            disableExport: true,
        },
        "b9ca1ec9-ab70-47ee-8c02-9e6db0358e9a": {
            field: "nextdepartmentname",
            headerName: "ISSUED DEPARTMENT NAME",
            headerAlign: "center",
            align: "center",
            minWidth: 150,
            flex: 1,
            renderCell: (params) => (
                params.row.next_department_id ?
                    <Box sx={{ minHeight: "auto", mt: 1 }}>
                        <Chip
                            avatar={<Avatar>{params.row.next_department_id}</Avatar>}
                            label={params.row.nextdepartmentname}
                            sx={{ margin: "0px 10px 10px 0px" }}
                        />
                    </Box> : '-'
            ),
            disableExport: true,
        },

    };

    const columnsWithoutPermission = [
        {
            field: "kapanid",
            headerName: "KAPANID",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 80,
            renderCell: (params) => (Utility.NeedToShowKapanDetailModal()) ?
                <Button
                    sx={{
                        textDecorationLine: "underline",
                        color: 'black'
                    }}
                    onClick={() => handleKapanDetailModal(params.value)}
                >
                    {params.value}
                </Button>
                :
                <div>{params.value}</div>,
        },
        {
            field: "packetid",
            headerName: "PACKETID",
            headerAlign: "center",
            align: "center",
            width: 80,
            renderCell: (params) => (Utility.NeedToShowPacketDetailModal()) ?
                <Button
                    sx={{
                        textDecorationLine: "underline",
                        color: 'black'
                    }}
                    onClick={() => handlePacketDetailModal(params.value, params.row.kapanid)}
                >
                    {params.value}
                </Button>
                :
                <div>{params.value}</div>,
        },
        {
            field: "processname",
            headerName: "CURRENT PROCESS",
            headerAlign: "center",
            align: "center",
            width: 250,
            renderCell: (params) => {
                if (params.row.processid == null || params.row.processname == null)
                    return '-';
                return (
                    <Box sx={{ minHeight: "auto", mt: 1 }}>
                        <Chip
                            avatar={<Avatar>{params.row.processid}</Avatar>}
                            label={params.row.processname}
                            sx={{ margin: "0px 10px 10px 0px" }}
                        />
                    </Box>
                )
            },
            disableExport: true,
        },
        {
            field: "receive",
            headerName: "RECEIVE",
            headerAlign: "center",
            align: "center",
            minWidth: 150,
            flex: 1,
            renderCell: (params) => (
                (Utility.isUpdateAllowed(local_Permission)) ?
                    <Button
                        startIcon={<RiUserReceived2Fill />}
                        sx={{ width: 150 }}
                        color='success'
                        variant="contained"
                        onClick={() => {
                            setShowReceiveModal(true)
                            setSourceData(params.row)
                            setLoadingButton(false)
                        }}
                    >
                        Receive
                    </Button> : '-'
            ),
            disableExport: true,
        },
    ];

    return (
        <div>

            <TabMenuSnackbar
                show={errorSnackbar}
                message={Constants.APiFailErrorMessage}
                variant={"error"}
                autoHide={Constants.SnackBarCloseTimer}
                title={"Error"}
                handleClose={() => setErrorSnackbar(false)}
            />

            <TabMenuSnackbar
                show={showSnackbar}
                message={snackbarMessage}
                variant={snackbarVariant}
                autoHide={Constants.SnackBarCloseTimer}
                title={snackbarVariant === "success" ? "Success" :
                    snackbarVariant === "error" ? "Failed" : "Warning"}
                handleClose={() => setShowSnackbar(false)}
            />

            {showPacketDetailModal ? (
                <PacketHistoryModal
                    show={showPacketDetailModal}
                    packetId={packetId}
                    kapanId={kapanId}
                    close={() => setShowPacketDetailModal(false)}
                />
            ) : null}
            {showKapanDetailModal ? (
                <KapanHistoryModal
                    show={showKapanDetailModal}
                    kapanId={kapanId}
                    close={() => setShowKapanDetailModal(false)}
                />
            ) : null}
            <Box
                sx={{
                    backgroundColor: '#fff',
                    color: '#000',
                    width: "100%",
                    position: "relative",
                    "& .MuiTablePagination-selectLabel": {
                        marginTop: "15px",
                    },
                    "& .MuiTablePagination-displayedRows": {
                        marginTop: "15px",
                    },
                    "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                        fontWeight: Constants.defaultFontWeight,
                    },
                    "& .css-cc8tf1": {
                        fontWeight: Constants.defaultFontWeight,
                    },
                    height: "77vh",
                }}
            >
                <DataGridPro
                    disableRowSelectionOnClick={true}
                    getRowHeight={() => "auto"}
                    sx={{
                        '& .MuiDataGrid-columnHeader': {
                            color: '#fff',
                            backgroundColor: 'grey',
                        },
                        "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                            py: "4px",
                        },
                        "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                            py: "12px",
                        },
                        "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                            py: "18px",
                        },
                        fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
                    }}
                    density="compact"
                    loading={approveRejectListLoader}
                    slots={{ toolbar: GridToolbar }}
                    rows={listData}
                    columns={columnNames}
                    getRowId={(row) => row.embededkey}
                    checkboxSelection={true}
                    onRowSelectionModelChange={(newSelectionModel) => {
                        const selectedIDs = new Set(newSelectionModel);
                        const selectedRowData = listData.filter((row) => selectedIDs.has(row.embededkey.toString()))
                        setSelectionModel(newSelectionModel);
                        setSourceData(selectedRowData)
                    }}
                    rowSelectionModel={selectionModel}
                    initialState={{
                        columns: {
                            columnVisibilityModel: {
                                // Hide columns id, the other columns will remain visible
                            },
                        },
                        sorting: {
                            sortModel: [{ field: "packetid", sort: "asc" }],
                        },
                        pagination: {
                            paginationModel: Constants.pageSize,
                        },
                    }}
                    pageSizeOptions={Constants.pageSizeOptions}
                    pagination
                    slotProps={{
                        toolbar: {
                            showQuickFilter: true,
                            quickFilterProps: { debounceMs: 500 },
                        },
                    }}
                />
                <Box sx={{
                    position: "absolute",
                    left: "0.4rem",
                    top: "1px"
                }}>
                    <CommonStoneSelection title={"Enter Multiple 'KapanId_PacketId'"} handleSelection={handleSelection} buttonDisabled={listData.length === 0} />
                </Box>
                <Box
                    sx={{
                        position: "absolute",
                        top: "11px",
                        left: "590px",
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '36rem',
                        color: "black",
                        fontWeight: 'normal',
                        fontSize: Constants.defaultTableFontSize
                    }}
                ><p style={{ margin: 0 }}>
                        <span>Total Packet:</span> <b>{totalPacket}</b> &nbsp;&nbsp;|&nbsp;&nbsp;
                        <span>Total Carats: </span> <b>{totalCarats.toFixed(2)}</b>  &nbsp;&nbsp;|&nbsp;&nbsp;
                        <span>Total Price: </span> <b>{totalPrice.toFixed(2)}</b>  &nbsp;&nbsp;|&nbsp;&nbsp;
                        <span>Avg Price: </span> <b>{isNaN(avgPrice) ? "0.00" : avgPrice.toFixed(2)}</b></p>
                </Box>
                <Box
                    style={{
                        position: "absolute",
                        top: 5,
                        right: "270px",
                        display: "flex",
                        flexWrap: "nowrap",
                    }}
                >
                    {approveRejectListLoader ? (
                        <CircularProgress size="1rem" />
                    ) : (
                        <Tooltip title="Reload List">
                            <RefreshOutlinedIcon
                                onClick={() => handleSearch()}
                                color="primary"
                                fontSize="medium"
                                sx={{ cursor: "pointer" }}
                            />
                        </Tooltip>
                    )}
                    {showReceiveModal && (
                        <AddPacketReceiveModal
                            show={showReceiveModal}
                            setShow={() => setShowReceiveModal(false)}
                            handleValidation={handleValidation}
                            handleSave={markReceive}
                            sourceData={sourceData}
                            loading={loadingButton}
                        />
                    )}

                </Box>
            </Box>
        </div>
    );
};

export default PacketReceiveMaster;
